import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import UnifiedHeader from '../common/UnifiedHeader';
import UnifiedFooter from '../common/UnifiedFooter';
import EcommerceCartDrawer from '../ecommerce/EcommerceCartDrawer';
import EcommerceAuthModal from '../ecommerce/EcommerceAuthModal';
import EcommerceWhatsAppFloating from '../ecommerce/EcommerceWhatsAppFloating';
import EcommerceCheckoutModal from '../ecommerce/EcommerceCheckoutModal';
import { useWebStore } from '../../context/WebStoreContext';

const ContactoPage = ({ user, onLogout }) => {
  const { storeConfig, landingConfig: globalLandingConfig, rubros, ecomCustomer: contextCustomer, setEcomCustomer: setContextCustomer, logoutCustomer } = useWebStore();
  const [config, setConfig] = useState(globalLandingConfig || null);

  useEffect(() => {
    if (globalLandingConfig) {
      setConfig(globalLandingConfig);
    }
  }, [globalLandingConfig]);

  // Estados de Carrito y Autenticación
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem('sigecom_ecom_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [ecomCustomer, setEcomCustomer] = useState(() => {
    try {
      const saved = localStorage.getItem('sigecom_ecom_customer');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  // Mantener ecomCustomer sincronizado con el contexto global
  useEffect(() => {
    setEcomCustomer(contextCustomer || null);
  }, [contextCustomer]);

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('register');

  // Formulario de Contacto
  const [contactForm, setContactForm] = useState({
    nombre: '',
    telefono: '',
    email: '',
    empresa: '',
    asunto: 'Consulta General',
    mensaje: ''
  });
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const totalCartCount = cartItems.reduce((acc, item) => acc + (Number(item.quantity) || 1), 0);
  const totalCartPrice = cartItems.reduce((acc, item) => acc + ((Number(item.unitPrice) || 0) * (Number(item.quantity) || 1)), 0);
  const activeCustomer = ecomCustomer || (user?.email ? { d_razon: user.username, d_email: user.email } : null);
  const canSeePrices = Boolean(storeConfig?.commercial?.publicPricesEnabled || activeCustomer);

  const contact = config?.contactSection || {
    address: 'Vicente Damonte 1237 / 1247, Lanús Este (B1824ALG), Buenos Aires',
    phone: '(011) 4247-6622',
    whatsapp: '5491142476622',
    email: 'ventas@hiperpel.com.ar',
    schedule: 'Lunes a Viernes de 08:00 a 18:00 hs | Sábados de 08:00 a 13:00 hs'
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    if (!contactForm.nombre.trim() || !contactForm.telefono.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Datos requeridos',
        text: 'Por favor completá tu nombre y teléfono de contacto.',
        confirmButtonColor: '#0d6efd'
      });
      return;
    }

    setIsSending(true);
    const targetPhone = (contact.whatsapp || '5491142476622').replace(/\D/g, '');
    const textMsg = `*CONSULTA WEB - HIPERPEL S.A.*\n\n` +
      `• *Nombre / Razón Social:* ${contactForm.nombre.trim()}\n` +
      (contactForm.empresa ? `• *Empresa / Comercio:* ${contactForm.empresa.trim()}\n` : '') +
      `• *Teléfono:* ${contactForm.telefono.trim()}\n` +
      (contactForm.email ? `• *Email:* ${contactForm.email.trim()}\n` : '') +
      `• *Asunto:* ${contactForm.asunto}\n` +
      `• *Mensaje:* ${contactForm.mensaje.trim() || 'Deseo solicitar asesoramiento y cotización.'}`;

    const whatsappUrl = `https://api.whatsapp.com/send?phone=${targetPhone}&text=${encodeURIComponent(textMsg)}`;
    window.open(whatsappUrl, '_blank');

    setIsSending(false);
    setContactForm({ nombre: '', telefono: '', email: '', empresa: '', asunto: 'Consulta General', mensaje: '' });
    Swal.fire({
      icon: 'success',
      title: '¡Mensaje generado!',
      text: 'Se abrió la conversación de WhatsApp con nuestro equipo de ventas.',
      confirmButtonColor: '#0d6efd'
    });
  };

  return (
    <div className="webstore-site-wrapper min-vh-100 d-flex flex-column" style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif', backgroundColor: 'var(--ecom-bg, #f8fafc)', color: 'var(--ecom-text, #1e293b)' }}>
      
      {/* Cabecera Unificada */}
      <UnifiedHeader
        activePage="contacto"
        cartCount={totalCartCount}
        cartTotal={totalCartPrice}
        currentUser={activeCustomer}
        onOpenCart={() => setIsCartOpen(true)}
        onLogout={() => {
          if (logoutCustomer) {
            logoutCustomer(true);
          } else {
            localStorage.removeItem('sigecom_ecom_customer');
            localStorage.removeItem('sigecom_ecom_last_activity');
            setEcomCustomer(null);
          }
        }}
        onAuthClick={(mode) => {
          setAuthModalMode(mode || 'register');
          setIsAuthModalOpen(true);
        }}
        priceListInfo="Lista Minorista"
        canSeePrices={canSeePrices}
        topBarConfig={config?.topBar}
        storeConfig={storeConfig}
        rubros={rubros}
      />

      {/* Banner Superior */}
      <div 
        className="text-white py-4 mb-4"
        style={{
          background: 'linear-gradient(135deg, var(--ecom-primary, #0d6efd) 0%, var(--ecom-secondary, #0a58ca) 100%)'
        }}
      >
        <div className="container">
          <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2">
            <div>
              <span className="badge bg-warning text-dark fw-bold px-3 py-1 rounded-pill text-uppercase mb-2">
                Atención al Cliente & Ventas
              </span>
              <h1 className="fw-black fs-2 mb-0">Contáctenos</h1>
            </div>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><a href="/" className="text-white opacity-75 text-decoration-none">Inicio</a></li>
                <li className="breadcrumb-item active text-white fw-bold" aria-current="page">Contáctenos</li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      {/* Contenido Principal de Contacto */}
      <main className="container flex-grow-1 py-4">
        <div className="row g-5 mb-5">
          
          {/* Información y Canales Rápidos */}
          <div className="col-lg-5 col-12">
            <span className="badge bg-primary-subtle text-primary fw-bold px-3 py-1.5 rounded-pill text-uppercase mb-3">
              Canales Directos
            </span>
            <h2 className="fw-extrabold text-dark mb-3">¿En qué podemos ayudarte?</h2>
            <p className="text-secondary small mb-4 lh-base">
              Comunicate con nuestros asesores comerciales por WhatsApp, teléfono o completando el formulario. Respondemos a la brevedad con cotizaciones y stock.
            </p>

            <div className="d-flex flex-column gap-3 mb-4">
              
              <div className="p-3 bg-light rounded-4 border d-flex align-items-center gap-3">
                <div className="bg-success-subtle text-success rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '40px', height: '40px', fontSize: '1.15rem' }}>
                  <i className="bi bi-whatsapp"></i>
                </div>
                <div className="flex-grow-1">
                  <h6 className="fw-bold mb-0 text-dark">WhatsApp Ventas</h6>
                  <span className="text-muted small">Atención inmediata por chat</span>
                  <div>
                    <a 
                      href={`https://wa.me/${contact.whatsapp.replace(/\D/g, '')}`} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="text-success fw-bold text-decoration-none"
                    >
                      +{contact.whatsapp}
                    </a>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-light rounded-4 border d-flex align-items-center gap-3">
                <div className="bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '40px', height: '40px', fontSize: '1.15rem' }}>
                  <i className="bi bi-telephone-fill"></i>
                </div>
                <div className="flex-grow-1">
                  <h6 className="fw-bold mb-0 text-dark">Línea Telefónica</h6>
                  <span className="text-muted small">Lunes a Viernes 08:00 a 18:00 hs</span>
                  <div>
                    <a href={`tel:${contact.phone}`} className="text-primary fw-bold text-decoration-none">
                      {contact.phone}
                    </a>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-light rounded-4 border d-flex align-items-center gap-3">
                <div className="bg-info-subtle text-info-emphasis rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '40px', height: '40px', fontSize: '1.15rem' }}>
                  <i className="bi bi-envelope-fill text-dark"></i>
                </div>
                <div className="flex-grow-1">
                  <h6 className="fw-bold mb-0 text-dark">Correo Electrónico</h6>
                  <span className="text-muted small">Consultas formales y facturación</span>
                  <div>
                    <a href={`mailto:${contact.email}`} className="text-dark fw-bold text-decoration-none">
                      {contact.email}
                    </a>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-light rounded-4 border d-flex align-items-center gap-3">
                <div className="bg-danger-subtle text-danger rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '40px', height: '40px', fontSize: '1.15rem' }}>
                  <i className="bi bi-geo-alt-fill"></i>
                </div>
                <div className="flex-grow-1">
                  <h6 className="fw-bold mb-0 text-dark">Planta & Retiro</h6>
                  <span className="text-muted small">{contact.address}</span>
                  <div>
                    <a href="/donde-estamos" className="text-danger fw-bold small text-decoration-none">
                      Ver mapa y cómo llegar &rarr;
                    </a>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* Formulario de Mensaje */}
          <div className="col-lg-7 col-12">
            <div className="bg-light p-4 p-lg-5 rounded-4 border shadow-sm">
              <h4 className="fw-extrabold text-dark mb-2">Envianos tu consulta o pedido de cotización</h4>
              <p className="text-muted small mb-4">
                Completá los siguientes campos y nuestro equipo comercial te responderá rápidamente.
              </p>

              <form onSubmit={handleContactSubmit}>
                <div className="row g-3">
                  
                  <div className="col-md-6 col-12">
                    <label className="form-label small fw-bold text-dark">Nombre y Apellido *</label>
                    <input
                      type="text"
                      className="form-control rounded-3 py-2"
                      placeholder="Ej: Juan Pérez"
                      value={contactForm.nombre}
                      onChange={(e) => setContactForm({ ...contactForm, nombre: e.target.value })}
                      required
                    />
                  </div>

                  <div className="col-md-6 col-12">
                    <label className="form-label small fw-bold text-dark">Empresa o Comercio (Opcional)</label>
                    <input
                      type="text"
                      className="form-control rounded-3 py-2"
                      placeholder="Ej: Distribuidora Sol"
                      value={contactForm.empresa}
                      onChange={(e) => setContactForm({ ...contactForm, empresa: e.target.value })}
                    />
                  </div>

                  <div className="col-md-6 col-12">
                    <label className="form-label small fw-bold text-dark">Teléfono / WhatsApp *</label>
                    <input
                      type="tel"
                      className="form-control rounded-3 py-2"
                      placeholder="Ej: 11 4247-6622"
                      value={contactForm.telefono}
                      onChange={(e) => setContactForm({ ...contactForm, telefono: e.target.value })}
                      required
                    />
                  </div>

                  <div className="col-md-6 col-12">
                    <label className="form-label small fw-bold text-dark">Correo Electrónico</label>
                    <input
                      type="email"
                      className="form-control rounded-3 py-2"
                      placeholder="ejemplo@correo.com"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label small fw-bold text-dark">Tipo de Consulta</label>
                    <select
                      className="form-select rounded-3 py-2"
                      value={contactForm.asunto}
                      onChange={(e) => setContactForm({ ...contactForm, asunto: e.target.value })}
                    >
                      <option value="Consulta General">Consulta General</option>
                      <option value="Cotización Mayorista">Cotización Mayorista / Fábrica</option>
                      <option value="Cajas a Medida">Cajas y Embalajes a Medida</option>
                      <option value="Línea Gastronómica">Línea Gastronómica & Descartables</option>
                      <option value="Estado de Pedido / Logística">Estado de Pedido / Logística</option>
                    </select>
                  </div>

                  <div className="col-12">
                    <label className="form-label small fw-bold text-dark">Mensaje / Detalle de los artículos requeridos</label>
                    <textarea
                      rows={4}
                      className="form-control rounded-3 py-2"
                      placeholder="Detallá los artículos, medidas aproximadas, cantidades y cualquier consulta..."
                      value={contactForm.mensaje}
                      onChange={(e) => setContactForm({ ...contactForm, mensaje: e.target.value })}
                    />
                  </div>

                  <div className="col-12 pt-2">
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg rounded-pill px-5 fw-bold shadow-sm w-100 w-sm-auto d-flex align-items-center justify-content-center gap-2"
                      disabled={isSending}
                    >
                      <i className="bi bi-send-fill"></i>
                      <span>{isSending ? 'Enviando...' : 'Enviar Consulta Comercial'}</span>
                    </button>
                  </div>

                </div>
              </form>
            </div>
          </div>

        </div>
      </main>

      {/* Footer Unificado */}
      <UnifiedFooter storeConfig={storeConfig} landingConfig={config} />

      {/* Modales Compartidos */}
      <EcommerceCartDrawer
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cartItems={cartItems}
        onUpdateQuantity={(key, qty) => {
          if (qty <= 0) setCartItems(prev => prev.filter(i => i.cartKey !== key));
          else setCartItems(prev => prev.map(i => i.cartKey === key ? { ...i, quantity: qty } : i));
        }}
        onRemoveItem={(key) => setCartItems(prev => prev.filter(i => i.cartKey !== key))}
        onClearCart={() => setCartItems([])}
        onCheckout={() => {
          setIsCartOpen(false);
          setIsCheckoutOpen(true);
        }}
        canSeePrices={canSeePrices}
        onLoginClick={() => {
          setIsCartOpen(false);
          setAuthModalMode('register');
          setIsAuthModalOpen(true);
        }}
      />

      <EcommerceCheckoutModal
        show={isCheckoutOpen}
        onHide={() => setIsCheckoutOpen(false)}
        cartItems={cartItems}
        canSeePrices={canSeePrices}
        currentUser={activeCustomer}
        whatsappSalesNumber={config?.contactSection?.whatsapp || '5491142476622'}
        companyName={storeConfig?.footer?.companyName || 'HIPERPEL S.A.'}
        storeConfig={storeConfig}
        companyId={storeConfig?.company_id || 1}
        onRequireAuth={() => {
          setIsCheckoutOpen(false);
          setAuthModalMode('register');
          setIsAuthModalOpen(true);
        }}
        onOrderCompleted={() => setCartItems([])}
      />

      <EcommerceAuthModal
        show={isAuthModalOpen}
        onHide={() => setIsAuthModalOpen(false)}
        initialMode={authModalMode}
        onSuccess={(customer) => {
          setEcomCustomer(customer);
          if (setContextCustomer) {
            setContextCustomer(customer);
          } else {
            localStorage.setItem('sigecom_ecom_customer', JSON.stringify(customer));
            localStorage.setItem('sigecom_ecom_last_activity', Date.now().toString());
          }
          setIsAuthModalOpen(false);
        }}
        companyId={storeConfig?.company_id || 1}
      />

      {config?.whatsappFloating?.enabled !== false && (
        <EcommerceWhatsAppFloating
          phoneNumber={config?.whatsappFloating?.phone || config?.contactSection?.whatsapp || '5491142476622'}
          defaultMessage={config?.whatsappFloating?.message || `¡Hola! Me comunico desde la sección de contacto de la web de ${config?.logoText || 'la empresa'}.`}
          showPill={config?.whatsappFloating?.showPill !== false}
          pillText={config?.whatsappFloating?.pillText || '¿Dudas? ¡Escribinos!'}
        />
      )}

    </div>
  );
};

export default ContactoPage;
